import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Tooltip,
  Typography,
} from '@mui/material'
import { OverrideRoundEligibilityPayload } from 'api/types'
import { CheckCircleIcon } from 'assets/icons/check-circle'
import ClearIcon from '@mui/icons-material/Clear'
import { FailIcon } from 'assets/icons/failIcon'
import { ChallengeGameFormat, ChallengeReasonRule } from 'utils/challenges'

interface UserChallengeRoundReason {
  reason?: string
  rule?: string
  eligible?: boolean
}

interface UserChallengeRoundDialogRow {
  gameName: string
  course: string
  gameFormat: string
  date: string
  roundId: string
  isQualified: boolean
  override: string
  reasons: UserChallengeRoundReason[]
}

interface Props {
  open: boolean
  isExcluded?: boolean
  row?: UserChallengeRoundDialogRow
  overrideStatus?: OverrideRoundEligibilityPayload['override']
  roundId: string
  onClose: () => void
  onToggle: (roundId: string, overrideStatus: OverrideRoundEligibilityPayload) => void
}

const UserChallengeRoundDialog = ({ open, onToggle, roundId, overrideStatus, onClose, isExcluded, row }: Props) => {
  const normalizedOverride = overrideStatus ?? 'ROUND_ELIGIBILITY_OVERRIDE_UNSPECIFIED'
  const reasons = row?.reasons ?? []

  const shouldShowExcludeButton = () => {
    if (isExcluded) {
      return (
        <Button
          sx={{
            border: 'solid 1px',
            borderRadius: '12px',
            color: '#FFF',
            background: '#1AA85D',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: '400',
            margin: '2px',
            minWidth: 'auto',
            padding: '6px 12px',
            '&:hover': {
              background: '#147A45',
              borderColor: '#147A45',
              boxShadow: '0 3px 6px rgba(22, 138, 77, 0.25)',
            },
          }}
          onClick={() => onToggle(roundId, { override: normalizedOverride })}
        >
          Include
        </Button>
      )
    }
    if (row?.isQualified) {
      return (
        <Button
          sx={{
            border: 'solid 1px',
            borderRadius: '12px',
            color: '#FFF',
            background: '#FF001F',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: '400',
            margin: '2px',
            minWidth: 'auto',
            padding: '6px 12px',
            '&:hover': {
              background: '#c7041b',
              borderColor: '#c7041b',
              boxShadow: '0 3px 6px rgba(22, 138, 77, 0.25)',
            },
          }}
          onClick={() => onToggle(roundId, { override: normalizedOverride })}
        >
          Remove
        </Button>
      )
    }
    return
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: 636,
          margin: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          borderRadius: '20px',
        },
      }}
    >
      <DialogTitle sx={{ background: '#FFF', display: 'flex', alignItems: 'center', mt: '12px' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>Add round to Challenge</Typography>
        <Button
          sx={{
            display: 'flex',
            justifyContent: 'center',
            color: '#8A8A8E',
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            flexShrink: 0,
            minWidth: 0,
            background: '#FFF',
            border: 'solid 1px',
            borderColor: '#E4E4E7',
            ml: 'auto',
          }}
          onClick={onClose}
        >
          <ClearIcon sx={{ fontSize: '18px', fontWeight: 400, lineHeight: 'normal' }} />
        </Button>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Typography>{row?.gameName}</Typography>
          <Typography variant="body2" sx={{ mb: 4 }}>
            {row?.course} · {ChallengeGameFormat[row ? row?.gameFormat : 'GOLF_GAME_FORMAT_UNSPECIFIED']} · {row?.date}
          </Typography>
        </Box>

        {reasons.length > 0 ? (
          <>
            <Typography variant="subtitle2" sx={{ mb: '4px', fontSize: '18px', fontWeight: '600' }}>
              Eligibility checks
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxHeight: 260,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 0.5,
                py: 0.5,
                pr: 1,
                m: 0,
                overscrollBehavior: 'contain',
                scrollbarWidth: 'thin',
                scrollbarColor: '#B4B9BE transparent',
                '&::-webkit-scrollbar': {
                  width: '3px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#B4B9BE',
                  borderRadius: '999px',
                  marginLeft: '1px',
                },
              }}
            >
              <List sx={{ width: '100%', p: 0 }}>
                {reasons
                  .sort((a, b) => Number(a.eligible) - Number(b.eligible))
                  .map((reason, index) => (
                    <ListItem key={`${reason.rule ?? 'reason'}-${index}`} disablePadding sx={{ display: 'list-item' }}>
                      <Tooltip
                        title={reason.reason}
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: '#222A35',
                              color: '#FFF',
                              fontSize: '16px',
                              padding: '20px',
                              fontWeight: '400',
                            },
                          },
                          arrow: {
                            sx: {
                              color: '#222A35',
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            width: '100%',
                            maxWidth: 636,
                            padding: '14px 12px',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '12px',
                            border: '1px solid #DFE4E1',
                            borderRadius: '12px',
                            mb: '6px',
                            ml: 0,
                            mr: 0.5,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {reason.eligible ? (
                              <CheckCircleIcon color="#098E4F" width={'22px'} height={'22px'} />
                            ) : (
                              <FailIcon />
                            )}
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '16px', color: reason.eligible ? '#098E4F' : '#B91C1C' }}
                            >
                              {ChallengeReasonRule[reason?.rule ?? 'ROUND_ELIGIBILITY_RULE_UNSPECIFIED']}
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    </ListItem>
                  ))}
              </List>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              maxWidth: 636,
              padding: '14px 12px',
              flexDirection: 'column',
              alignItems: 'flex-start',

              border: '1px solid #DFE4E1',
              borderRadius: '8px',
            }}
          >
            <Typography sx={{ fontSize: '18px', fontWeight: '600' }}>Included by core</Typography>
            <Typography variant="body2">This round is included in the challenge calculation.</Typography>
          </Box>
        )}
        {!row?.isQualified && !isExcluded && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              maxWidth: 636,
              padding: '14px 12px',
              flexDirection: 'column',
              alignItems: 'flex-start',

              border: '1px solid #DFE4E1',
              borderRadius: '8px',
              mb: 1,
              marginTop: '20px',
            }}
          >
            <Typography sx={{ fontSize: '18px', fontWeight: '600' }}>Excluded by core</Typography>
            <Typography variant="body2">This round is not included in the challenge calculation.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          sx={{
            border: 'solid 1px #F3F4F6',
            borderColor: '#eceef1',
            borderRadius: '12px',
            color: 'black',
            background: '#F3F4F6',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: '400',
            margin: '2px',
            minWidth: 'auto',
            padding: '6px 12px',
          }}
          onClick={onClose}
        >
          {isExcluded ? 'Cancel' : 'Close'}
        </Button>
        {shouldShowExcludeButton()}
      </DialogActions>
    </Dialog>
  )
}

export default UserChallengeRoundDialog
